// Library Methods
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Styles
import { useTheme } from "@mui/material/styles";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import RecentExams from "src/components/containers/Dashboard/RecentlyCompletedAccordion/RecentExams/RecentExams";
import RecentProgressionAnalyses from "src/components/containers/Dashboard/RecentlyCompletedAccordion/RecentProgressionAnalyses/RecentProgressionAnalyses";
import TimeRangeSwitcher from "src/components/containers/Dashboard/RecentlyCompletedAccordion/TimeRangeSwitcher/TimeRangeSwitcher";
import ExpandableSearchbar from "src/components/shared/inputs/ExpandableSearchbar/ExpandableSearchbar";
import { ChevronDownIcon } from "src/assets/icons";

// Hooks
import useDebounce from "src/hooks/input/useDebounce";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type RecentlyCompletedAccordionType = {
  timeRange: number;
  setTimeRange: React.Dispatch<React.SetStateAction<number>>;
  onLoadingChange?: (isLoading: boolean) => void;
};

const RecentlyCompletedAccordion = ({
  timeRange,
  setTimeRange,
  onLoadingChange,
}: RecentlyCompletedAccordionType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const theme = useTheme();
  const { dividerBorder } = getSurfaceTokens(theme);

  const canUseProgressionReports = Boolean(
    clinicInfo?.licensing?.canUseProgressionReports
  );

  // RecentProgressionAnalyses isn't rendered at all when the clinic can't
  // use progression reports, so its loading state shouldn't be able to
  // block the combined signal below in that case.
  const [isLoadingRecentExams, setIsLoadingRecentExams] = useState(true);
  const [
    isLoadingRecentProgressionAnalyses,
    setIsLoadingRecentProgressionAnalyses,
  ] = useState(true);

  const isLoading = useMemo(
    () =>
      isLoadingRecentExams ||
      (canUseProgressionReports && isLoadingRecentProgressionAnalyses),
    [
      isLoadingRecentExams,
      isLoadingRecentProgressionAnalyses,
      canUseProgressionReports,
    ]
  );

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  // Reset the sub-accordions on reopening the parent accordion
  const [outerExpanded, setOuterExpanded] = useState(true);
  const [subAccordionsResetKey, setSubAccordionsResetKey] = useState(0);

  const handleOuterChange = (_: React.SyntheticEvent, isExpanded: boolean) => {
    setOuterExpanded(isExpanded);
    if (isExpanded) setSubAccordionsResetKey((prev) => prev + 1);
  };

  // Shared between RecentExams and RecentProgressionAnalyses -- debounced
  // once here rather than in each of them separately.
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm);

  const onSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchTerm(event?.target?.value);
  };

  const clearSearchFilter = () => {
    setSearchTerm("");
  };

  return (
    <Accordion
      expanded={outerExpanded}
      onChange={handleOuterChange}
      disableGutters
      sx={{
        p: 0,
        m: 0,
        boxShadow: "none",
        backgroundColor: "transparent",
        backgroundImage: "none",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ChevronDownIcon style={{ color: "#FFF" }} />}
        sx={{
          p: 0,
          m: 0,
          px: "8px",
          minHeight: "auto",
          border: dividerBorder,
          borderRadius: "4px",
          backgroundColor: "#202338",
          "&.Mui-expanded": {
            p: 0,
            m: 0,
            px: "8px",
            minHeight: "auto",
          },
        }}
      >
        <Typography
          alignContent="center"
          fontSize="16px"
          fontWeight={600}
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
          maxWidth="100%"
          color="#FFF"
        >
          {t("Recently Completed")}
        </Typography>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          p: 0,
          m: 0,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <Box display="flex" justifyContent="flex-end" mt="8px" gap="8px">
          <ExpandableSearchbar
            id="recently-completed-searchbar"
            isLoading={false}
            searchTerm={searchTerm}
            onChange={onSearchChange}
            onClear={clearSearchFilter}
            searchTooltip={t("Search Recently Completed")}
            openByDefault={true}
          />
          <TimeRangeSwitcher
            isLoading={false}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
          />
        </Box>
        <Box
          display="flex"
          flexDirection={{ xs: "column", lg: "row" }}
          alignItems="stretch"
          gap="12px"
        >
          <Box flex={1} minWidth={0} width="100%">
            <RecentExams
              timeRange={timeRange}
              searchTerm={debouncedSearchTerm}
              onLoadingChange={setIsLoadingRecentExams}
              forceExpandKey={subAccordionsResetKey}
            />
          </Box>
          {canUseProgressionReports && (
            <Box flex={1} minWidth={0} width="100%">
              <RecentProgressionAnalyses
                timeRange={timeRange}
                searchTerm={debouncedSearchTerm}
                onLoadingChange={setIsLoadingRecentProgressionAnalyses}
                forceExpandKey={subAccordionsResetKey}
              />
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default RecentlyCompletedAccordion;
